import React from "react";
import { FormattedDate } from "react-intl";
import { gql, useMutation } from "@apollo/client";

import { Box, Alert, Typography, Divider, Stack } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { TruckDelivery as ShipmentIcon } from "mdi-material-ui";

const Icon = () => <ShipmentIcon />;

const Title = ({ orderEvent: { shipment } }) => <span>Éxpédition</span>;

// https://profil.mydelivengo.laposte.fr/courrier/documents-douaniers/details/envoi/8882137
// https://profil.mydelivengo.laposte.fr/courrier/envois/preparer-impression?id=8882249

const fragments = {
  orderShipment: gql`
    fragment Orders_OrderEvent_ShipmentEvent_OrderShipment on OrderShipment {
      id
      createdAt
      updatedAt

      colissimo {
        labelFile {
          id
          url
        }
      }

      delivengo {
        envoiId
        pliId

        supportFile {
          id
          url
        }
        customsDeclaration {
          id
          url
        }
      }

      ups {
        file {
          id
          url
        }

        alerts {
          code
          description
        }
      }

      fedex {
        labelFile {
          id
          url
        }

        notifications {
          severity
          message
        }
      }

      trackingId
      trackingUrl

      shipup {
        orderUrl
        trackerUrl
      }
    }
  `,
};

fragments.orderEvent = gql`
  fragment Orders_OrderEvent_ShipmentEvent_OrderEvent on OrderEvent {
    id

    ... on OrderEvent_Shipment {
      shipment {
        id

        ...Orders_OrderEvent_ShipmentEvent_OrderShipment
      }
    }
  }

  ${fragments.orderShipment}
`;

const MUTATION = gql`
  mutation ($orderShipmentId: ID!) {
    PullOrderShipmentDelivengoTrackingInfos(orderShipmentId: $orderShipmentId) {
      orderShipment {
        id

        ...Orders_OrderEvent_ShipmentEvent_OrderShipment
      }
    }
  }

  ${fragments.orderShipment}
`;

function Content({ orderEvent: { shipment } }) {
  const [pullTrackingInfos, { loading }] = useMutation(MUTATION);

  return (
    <Stack spacing={1}>
      {shipment.colissimo && (
        <>
          {shipment.colissimo.labelFile && (
            <Typography>
              Document :{" "}
              <a
                href={shipment.colissimo.labelFile.url}
                target="_blank"
                rel="noopener noreferrer"
              >
                lien
              </a>
            </Typography>
          )}

          <Divider />
        </>
      )}

      {shipment.delivengo && (
        <>
          {shipment.delivengo.envoiId && (
            <Typography>
              ID Envoi Delivengo : <code>{shipment.delivengo.envoiId}</code>
            </Typography>
          )}

          {shipment.delivengo.pliId && (
            <Typography>
              ID Pli Delivengo : <code>{shipment.delivengo.pliId}</code>
            </Typography>
          )}

          {shipment.delivengo.supportFile && (
            <Typography>
              Document support :{" "}
              <a
                href={shipment.delivengo.supportFile.url}
                target="_blank"
                rel="noopener noreferrer"
              >
                lien
              </a>
            </Typography>
          )}

          {shipment.delivengo.customsDeclaration && (
            <Typography>
              Déclaration de douane :{" "}
              <a
                href={shipment.delivengo.customsDeclaration.url}
                target="_blank"
                rel="noopener noreferrer"
              >
                lien
              </a>
            </Typography>
          )}

          <Divider />
        </>
      )}

      {shipment.ups && (
        <>
          {shipment.ups.alerts?.map(({ description }, idx) => (
            <Alert severity="warning" key={idx}>
              {description}
            </Alert>
          ))}

          {shipment.ups.file && (
            <Typography>
              Document :{" "}
              <a
                href={shipment.ups.file.url}
                target="_blank"
                rel="noopener noreferrer"
              >
                lien
              </a>
            </Typography>
          )}

          <Divider />
        </>
      )}

      {shipment.fedex && (
        <>
          {shipment.fedex.notifications?.map(({ severity, message }, idx) => (
            <Alert severity={severity} key={idx}>
              {message}
            </Alert>
          ))}

          {shipment.fedex.labelFile && (
            <Typography>
              Document :{" "}
              <a
                href={shipment.fedex.labelFile.url}
                target="_blank"
                rel="noopener noreferrer"
              >
                lien
              </a>
            </Typography>
          )}

          <Divider />
        </>
      )}

      {shipment.trackingId && (
        <Typography>
          Tracking ID : <code>{shipment.trackingId}</code>
        </Typography>
      )}

      {shipment.trackingUrl && (
        <Typography>
          Tracking URL :{" "}
          <a
            href={shipment.trackingUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            {shipment.trackingUrl}
          </a>
        </Typography>
      )}

      {shipment.updatedAt !== shipment.createdAt && (
        <Typography align="right" color="textSecondary">
          <small>
            Dernière mise à jour :{" "}
            <FormattedDate
              value={shipment.updatedAt}
              year="numeric"
              month="long"
              day="2-digit"
              hour="2-digit"
              minute="2-digit"
            />
          </small>
        </Typography>
      )}

      {!!shipment.delivengo && (
        <Box sx={{ textAlign: "right" }}>
          <LoadingButton
            onClick={() => {
              pullTrackingInfos({
                variables: {
                  orderShipmentId: shipment.id,
                },
              }).catch(() => {});
            }}
            loading={loading}
            size="small"
            variant="text"
            color="secondary"
          >
            Mettre à jour les infos de tracking
          </LoadingButton>
        </Box>
      )}

      {!!shipment.shipup && (
        <>
          <Divider />

          <Typography variant="h4" color="primary">
            Shipup
          </Typography>

          <Typography>
            Tracking commande :{" "}
            <a
              href={shipment.shipup.orderUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              {shipment.shipup.orderUrl}
            </a>
          </Typography>

          <Typography>
            Tracking envoi :{" "}
            <a
              href={shipment.shipup.trackerUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              {shipment.shipup.trackerUrl}
            </a>
          </Typography>
        </>
      )}
    </Stack>
  );
}

const Event = {
  Icon,
  Title,
  Content,
  fragments,
};

export default Event;
